import './index.scss';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AiFillMessage, AiFillDelete, AiFillEdit } from 'react-icons/ai';
import { Header } from 'src/component/Layout';
import { Select } from 'src/component/Select';
import { TeachersComment, Users } from 'src/assets/icon';
import { AppLabel, AppSpan, AppText, FlexColumn, FlexRow, RoundImageContainer, TextInput, Button, Form } from 'src/style';
import ImageFallback from "src/assets/img/image-fallback.png";
import helpers, {
    getClassList,
    getStudentsList,
    getAcademicSessions,
    getStudentByUserName,
    getAllStudentsInClass,
    getIndex,
} from 'src/utils/helpers';
import AppInfoContext from 'src/provider/state-manager/appInfoProvider';
import { resultType, terms, subjects } from 'src/provider/config/constant';
import VisibilityContext from 'src/provider/state-manager/visibilityProvider';
import ApiContext from 'src/provider/API/call-service';
import { MemberTypeEnum, ResultTypeEnum } from 'src/model';
import { DbResult, IResultList, UploadResultsProps } from './types';
import { InputTable } from './InputTable';

export const UploadResults = ({ user, isEditResult, setEditResult }: UploadResultsProps) => {
    const { API } = useContext(ApiContext);
    const { info } = useContext(AppInfoContext);
    const { notifier } = useContext(VisibilityContext);
    const [data, setData] = useState({
        studentName: "", session: "", term: "", resultType: "", teacherComment: "", teacherName: "",
        studentClass: "", result: [] as IResultList[], memberType: "", resultId: "", updatedResult: [] as IResultList[],
    });
    const [resultItem, setResultItem] = useState({ subject: "", score: "" });
    const sessions = getAcademicSessions();
    const allStudents = getStudentsList(info);
    const studentList = getAllStudentsInClass(allStudents, user?.profile?.memberClass);
    const studentProfile = getStudentByUserName(info, data.studentName)
    const [resultId, setResultId] = useState("");

    useEffect(() => {
        if (user.profile) {
            setData({ ...data, teacherName: user.profile.firstName, studentClass: user.profile.memberClass })
        }
    }, [user])

    const handleSelect = useCallback((key: string, value: string) => {
        setData({ ...data, [key]: value });
    }, [data])

    const handleSubject = useCallback((key: string, value: string) => {
        setResultItem({ ...resultItem, [key]: value })
    }, [resultItem])

    const handleDelete = useCallback((id: number, resultList: IResultList[]) => {
        let updatedResult: IResultList[] = [];
        let sn = 1;
        resultList.forEach(result => {
            if (result.sn != id) {
                result.sn = sn
                updatedResult.push(result)
                sn++
            }
        });

        setData({ ...data, result: updatedResult });
    }, [data]);

    const handleUpload = useCallback(async (e) => {
        e.preventDefault();
        if (data.result.length) {
            const validation = helpers.inputValidator(data, ["teacherComment", "memberType", "resultId"]);
            if (!validation.isValidated) return notifier.show(validation.message, 'Validation Response');
            const result = await API.uploadResult(data);
            if (result) return setData({ ...data, result: [] });
        } else {
            return notifier.show('Please add score', 'Validation Response');
        }

    }, [data]);

    // 
    // ****** Edit result functions ******
    // 
    useEffect(() => {
        if (isEditResult && info) {
            setData({
                ...data,
                memberType: MemberTypeEnum.ADMIN,
                term: info?.result?.term,
                session: info?.result?.session,
                studentName: info?.result?.studentProfile?.username,
                studentClass: info?.result?.studentProfile?.memberClass,
            })
        }
    }, [isEditResult, info]);

    useEffect(() => {
        if (data.resultType) {
            setData({ ...data, resultId: resultId });
        }
    }, [data.resultType, resultId])

    useEffect(() => {
        const updatedResult: IResultList[] = [];
        info?.result?.result?.map((result: DbResult) => {
            const availableResult = data?.result?.find(res => res.subject === result.subject);
            if (data.resultType === ResultTypeEnum.TEST && !availableResult) {
                const res = { subject: result.subject, score: result.testScore! }
                updatedResult.push(res)
            } else if (data.resultType === ResultTypeEnum.EXAM && !availableResult) {
                const res = { subject: result.subject, score: result.examScore! }
                updatedResult.push(res)
            }
        })

        if (data.resultType) {
            if (data.resultType === ResultTypeEnum.TEST) {
                setResultId(info?.result?.resultID?.testId)
            } else if (data.resultType === ResultTypeEnum.EXAM) {
                setResultId(info?.result?.resultID?.examId)
            };
        }

        if (data.result.length) {
            setData({ ...data, updatedResult: [...updatedResult, ...data.result] })
        }

    }, [data.resultType, data.result]);

    const handleEdit = useCallback(async (e) => {
        e.preventDefault();
        if (data.result.length) {
            if (data.resultId) {
                const validation = helpers.inputValidator(data, ["teacherComment", "teacherName"]);
                if (!validation.isValidated) return notifier.show(validation.message, 'Validation Response');
                const result = await API.editResult(data);
                if (result && setEditResult) {
                    setEditResult(false);
                    setData({ ...data, result: [] })
                }
            } else {
                return notifier.show(`${data.resultType} result to be updated did not exist`)
            }
        } else {
            return notifier.show('Please add score', 'Validation Response');
        }
    }, [data]);

    const handleAddResult = useCallback((e) => {
        e.preventDefault();
        let isAdded = false
        const validation = helpers.inputValidator(resultItem);
        if (!validation.isValidated) return notifier.show(validation.message, 'Validation Response');
        data.result?.forEach(result => {
            if (result.subject === resultItem.subject) {
                notifier.show('Subject is already picked', 'Validation Response');
                isAdded = true
            }
        })
        if (isAdded) return;
        const newArr = [...data.result, { ...resultItem, sn: data.result.length + 1 }]
        setData({ ...data, result: newArr })
    }, [resultItem, data]);

    return (
        <div className='upload-results'>
            <Header user={user}>
                <FlexRow gap='2' className='grid'>
                    {isEditResult ? null :
                        (<Select
                            className='select'
                            label='Student'
                            Icon={<Users />}
                            name='studentName'
                            list={studentList}
                            labelColor='#FFF5E5'
                            handleSelect={handleSelect}
                            placeholder='Select Student'
                        />)
                    }
                    {data.studentName || isEditResult ? (
                        <Select
                            className='select'
                            label='Session'
                            list={sessions}
                            name='session'
                            labelColor='#FFF5E5'
                            handleSelect={handleSelect}
                            placeholder='Select Session'
                            defaultValue={getIndex(sessions, data.session)}
                        />
                    ) : null}
                    {data.session ? (
                        <Select
                            className='select'
                            label='Term'
                            name='term'
                            list={terms}
                            labelColor='#FFF5E5'
                            placeholder='Select Term'
                            handleSelect={handleSelect}
                            defaultValue={getIndex(terms, data.term)}
                        />
                    ) : null}
                    {data.term ? (
                        <Select
                            className='select'
                            label='Result Type'
                            name='resultType'
                            list={resultType}
                            labelColor='#FFF5E5'
                            placeholder='Select Result Type'
                            handleSelect={handleSelect}
                        />
                    ) : null}
                </FlexRow>
                {data.resultType && !isEditResult ? (
                    <TextInput
                        topPadding='1.7'
                        leftPadding='1.7'
                        rightPadding='1.7'
                        bottomPadding='1.7'
                        topMargin='2.4'
                        gap='1.6'
                        justifyContent='flex-start'
                        className='comment-container'
                    >
                        <TeachersComment />
                        <input value={data.teacherComment} onChange={(e) => setData({ ...data, teacherComment: e.target.value })} type="text" placeholder='Teacher’s Comment' />
                    </TextInput>
                ) : null}
            </Header>
            {data.resultType ?
                (<div className="upload-details">
                    <FlexRow
                        className='header-flex'
                        justifyContent='space-between'
                        bottomMargin='2.4'
                    >
                        <FlexRow className='header-left-content' alignItems='top' justifyContent='flex-start' gap='5'>
                            <FlexRow justifyContent='flex-start' gap='2'>
                                <RoundImageContainer className='img-container' size='5' isMaxWidth>
                                    <img src={studentProfile?.avater || ImageFallback} alt='user avatar' />
                                </RoundImageContainer>

                                <FlexColumn width='50' sizeUnit='%' alignItems='flex-start'>
                                    <AppText fontWeight='600' color='#712126'>
                                        {studentProfile?.firstName || 'STU003'}
                                    </AppText>

                                    <AppText textSize='1.4' fontWeight='400' color='#374957'>
                                        ID: <AppSpan textSize='1.4' color='#374957' fontWeight='600'>
                                            {studentProfile?.regId || 'STU003'}
                                        </AppSpan>
                                    </AppText>
                                </FlexColumn>
                            </FlexRow>

                            <AppText className='center-title' color='#374957' textSize='1.4'>
                                Student Results
                            </AppText>
                        </FlexRow>
                    </FlexRow>
                    <Form>
                        <FlexRow bottomMargin='2.5' className='action-inputs' gap='4' alignItems='end'>
                            <Select
                                className='select select-subject'
                                label='Subject'
                                Icon={<Users />}
                                name='subject'
                                list={subjects}
                                labelColor='#712126'
                                handleSelect={handleSubject}
                                placeholder='Select Subject'
                                selectInputLimit={50}
                            />
                            <div className='score-input'>
                                <label htmlFor="score">Score</label>
                                <TextInput borderColor='#8A8A8A' topPadding='1.7' leftPadding='1.7' rightPadding='1.7' bottomPadding='1.7'>
                                    <input onChange={(e) => setResultItem({ ...resultItem, score: e.target.value })} type="number" placeholder='Enter Score' className='input-number' />
                                </TextInput>
                            </div>
                            <Button
                                borderColor='#A77B37'
                                color='#ffff'
                                bgColor='#A77B37'
                                onClick={handleAddResult}
                                className='add-score-btn'
                                fontWeight='500'
                                titleSize='1.4'
                                height='5.5'
                                leftPadding='1.7'
                                rightPadding='1.7'
                            >
                                Add Score
                            </Button>
                            <Button
                                color='#ffff'
                                borderColor='#712126'
                                bgColor='#712126'
                                onClick={isEditResult ? handleEdit : handleUpload}
                                className='add-score-btn'
                                fontWeight='500'
                                titleSize='1.4'
                                height='5.5'
                                leftPadding='1.7'
                                rightPadding='1.7'
                            >
                                {isEditResult ? "Edit Result" : "Upload Result"}
                            </Button>
                        </FlexRow>
                    </Form>

                    <InputTable result={data.result} handleDelete={handleDelete} />
                </div>) : null
            }
        </div>
    )
}
